import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Calculator,
  ArrowRight,
  CheckCircle,
  Shield,
  Phone,
  Award,
  Palette,
  Clock,
  Factory
} from 'lucide-react';
import { Link } from 'react-router-dom';

const UKBasswoodPage = () => {
  const features = [
    { text: 'Manufactured in the UK', icon: Factory },
    { text: 'Premium Quality Basswood', icon: Award },
    { text: '4-5 Week Lead Time', icon: Clock },
    { text: 'Superior Finish & Craftsmanship', icon: Award },
    { text: 'Smooth Grain Finish', icon: Award },
    { text: 'Harder Impact Resistance', icon: Shield },
    { text: 'Available in 30+ Colours', icon: Palette },
    { text: '6 Natural Wood Stains Available', icon: Palette },
    { text: 'Custom Colours Available', icon: Palette },
    { text: '10 Year Structural Warranty', icon: Shield }
  ];

  const bestFor = [
    'Luxury homes',
    'Heritage properties',
    'Bespoke projects',
    'Time-sensitive installations',
    'Master bedrooms',
    'Grand living spaces'
  ];

  const faqs = [
    {
      question: 'What makes UK Made Basswood different?',
      answer: 'Our UK Made Basswood shutters are manufactured right here in the United Kingdom. This means exceptional quality control, shorter lead times of just 4-5 weeks, and the finest craftsmanship. Every shutter is made to your exact specifications by skilled British craftspeople.'
    },
    {
      question: 'Why is the lead time shorter?',
      answer: 'Because the shutters are manufactured in the UK rather than overseas, we can deliver them in just 4-5 weeks. This makes UK Made Basswood ideal for time-sensitive projects or when you simply cannot wait to transform your home.'
    },
    {
      question: 'Is UK Made Basswood better quality than standard Basswood?',
      answer: 'Both use premium basswood timber. The UK manufacturing process offers tighter quality control and the ability to handle bespoke requirements more easily. The finish and craftsmanship are exceptional, with every detail inspected before delivery.'
    },
    {
      question: 'Can I visit the manufacturing facility?',
      answer: 'We can arrange visits to our UK manufacturing partner for customers who would like to see the craftsmanship firsthand. Please speak to your surveyor or contact us to arrange this.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="relative py-20" style={{background: 'linear-gradient(to bottom right, #eff6ff, #e0f2fe)'}}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-100 mb-6">
                <Factory className="h-5 w-5 text-sky-600" />
                <Badge className="bg-sky-600 text-white">UK Manufactured</Badge>
                <Badge className="bg-amber-500 text-white ml-2">Premium</Badge>
              </div>
              
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                UK Made Basswood Shutters
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Premium basswood shutters manufactured in the United Kingdom. Exceptional craftsmanship, 
                superior quality control, and a shorter 4-5 week lead time for those who want the very best.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                  <Shield className="h-5 w-5 text-sky-600" />
                  <span className="font-semibold text-gray-900">10 Year Warranty</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                  <Clock className="h-5 w-5 text-sky-600" />
                  <span className="font-semibold text-gray-900">4-5 Week Lead Time</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                  <Factory className="h-5 w-5 text-sky-600" />
                  <span className="font-semibold text-gray-900">Made in the UK</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/calculator">
                  <Button size="lg" className="bg-sky-600 hover:bg-sky-700 text-white px-8 py-4 text-lg">
                    <Calculator className="mr-2 h-5 w-5" />
                    Use Our Online Estimator
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-sky-600 text-sky-600 hover:bg-sky-50 px-8 py-4 text-lg">
                    <Phone className="mr-2 h-5 w-5" />
                    Book Free Survey
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -top-4 -right-4 bg-sky-600 text-white px-4 py-2 rounded-full font-semibold shadow-lg z-10">
                Made in the UK
              </div>
              <img 
                src="https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/leqxhvi8_01.%20Taupe%20Bay%20Shutters.jpg"
                alt="UK Made Basswood Shutters"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-sky-600">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-white text-center">
            <div className="flex flex-col items-center gap-2">
              <Factory className="h-8 w-8" />
              <span className="font-semibold">UK Manufactured</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Clock className="h-8 w-8" />
              <span className="font-semibold">4-5 Week Lead Time</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Palette className="h-8 w-8" />
              <span className="font-semibold">30+ Colours</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Shield className="h-8 w-8" />
              <span className="font-semibold">10 Year Warranty</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Premium Features</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              UK Made Basswood shutters deliver the finest quality with British craftsmanship.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-sky-50">
                      <feature.icon className="h-6 w-6 text-sky-600" />
                    </div>
                    <span className="text-gray-700 font-medium pt-2">{feature.text}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Perfect For</h2>
              <p className="text-lg text-gray-600 mb-8">
                UK Made Basswood shutters are the choice for homeowners who demand the highest quality 
                and appreciate fine British craftsmanship. Ideal for luxury homes and bespoke projects.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {bestFor.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm">
                    <CheckCircle className="h-5 w-5 text-sky-600" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/leqxhvi8_01.%20Taupe%20Bay%20Shutters.jpg"
                alt="UK Made Basswood Shutters in Home"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Common questions about our UK Made Basswood Shutters</p>
          </div>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 text-white bg-sky-700">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">The Finest British-Made Shutters</h2>
          <p className="text-xl mb-8 opacity-90">
            Get an instant estimate for UK Made Basswood Shutters using our online calculator.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-white hover:bg-gray-100 text-sky-700 px-8 py-4 text-lg">
                <Calculator className="mr-2 h-5 w-5" />
                Use Our Online Estimator
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/shutters/materials">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-4 text-lg">
                Compare All Ranges
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UKBasswoodPage;
